/**
 * Currency helper — global formatter that uses the current user's country.
 *
 * Replaces hardcoded ₹ / Rs strings across the CRM so Thailand users see ฿,
 * USA users see $, etc.
 *
 * Usage:
 *   import { formatCurrency, useCurrency } from "@/utils/currency";
 *   formatCurrency(1500, { country_code: "IN" })  // "₹1,500"
 *   formatCurrency(1500, user)                     // resolves from user
 *
 *   // Inside a React component (auto-pulls user from AuthContext):
 *   const { format, symbol, code } = useCurrency();
 *   format(1500); // "₹1,500" or "฿1,500" based on logged-in user
 */
import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";

const CURRENCY_MAP = {
  IN: { symbol: "₹", code: "INR", locale: "en-IN" },
  TH: { symbol: "฿", code: "THB", locale: "th-TH" },
  MY: { symbol: "RM", code: "MYR", locale: "ms-MY" },
  US: { symbol: "$", code: "USD", locale: "en-US" },
  AE: { symbol: "AED", code: "AED", locale: "ar-AE" },
};

const DEFAULT = CURRENCY_MAP.IN;

export function getCurrencyMeta(userOrCode) {
  if (!userOrCode) return DEFAULT;
  const code = typeof userOrCode === "string"
    ? userOrCode.toUpperCase()
    : (userOrCode?.country_code || userOrCode?.country_id || "IN").toString().toUpperCase();
  return CURRENCY_MAP[code] || DEFAULT;
}

export function formatCurrency(amount, userOrCode, options = {}) {
  const meta = getCurrencyMeta(userOrCode);
  const value = Number(amount || 0);
  const { decimals = 0, compact = false } = options;

  if (compact && Math.abs(value) >= 1000) {
    if (Math.abs(value) >= 10_000_000) return `${meta.symbol}${(value / 10_000_000).toFixed(1)}Cr`;
    if (Math.abs(value) >= 100_000) return `${meta.symbol}${(value / 100_000).toFixed(1)}L`;
    if (Math.abs(value) >= 1000) return `${meta.symbol}${(value / 1000).toFixed(1)}K`;
  }

  try {
    const formatter = new Intl.NumberFormat(meta.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${meta.symbol}${formatter.format(value)}`;
  } catch {
    return `${meta.symbol}${value.toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }
}

export function currencySymbol(userOrCode) {
  return getCurrencyMeta(userOrCode).symbol;
}

/**
 * Read the logged-in user from localStorage/sessionStorage.
 * Mirrors the storage strategy used by AuthContext.
 * Returns null if no user is stored (e.g. login page).
 */
function getStoredUser() {
  try {
    const raw = localStorage.getItem("user") || sessionStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Module-level currency symbol — reads user from storage.
 * Use this in pure helpers / non-React code where no `user` is in scope.
 */
export function activeCurrencySymbol() {
  return getCurrencyMeta(getStoredUser()).symbol;
}

/**
 * Module-level formatter — auto-resolves the user from storage.
 * Drop-in replacement for `(v) => \`₹\${v}\`` style local helpers.
 */
export function formatCurrencyAuto(amount, options = {}) {
  return formatCurrency(amount, getStoredUser(), options);
}

/**
 * React hook — pulls the current user from AuthContext and returns currency utils.
 * Falls back to IN/₹ when no user is logged in (e.g. login screen).
 */
export function useCurrency() {
  const { user } = useAuth() || {};
  return useMemo(() => {
    const meta = getCurrencyMeta(user);
    return {
      symbol: meta.symbol,
      code: meta.code,
      locale: meta.locale,
      format: (amount, options) => formatCurrency(amount, user, options),
    };
  }, [user]);
}
